import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { TIERS, ZONE_LABEL, RATES, aud } from "@/lib/catalog";
import { api } from "@/lib/api";
import { Eyebrow, Reveal, Stepper } from "./Reveal";

export const Postage = () => {
  const [postcode, setPostcode] = useState("");
  const [qty, setQty] = useState(3);
  const [live, setLive] = useState(null);
  const [err, setErr] = useState("");
  const valid = /^\d{4}$/.test(postcode);

  const fetchQuote = (pc, q) => {
    if (!/^\d{4}$/.test(pc)) return;
    api.get("/postage", { params: { postcode: pc, qty: q } })
      .then(({ data }) => { setLive(data); setErr(""); })
      .catch((e) => { setLive(null); setErr(e?.response?.data?.detail || "Couldn't get a price — check the postcode."); });
  };
  const onPostcode = (v) => { setPostcode(v); setLive(null); fetchQuote(v, qty); };
  const onQty = (q) => { setQty(q); fetchQuote(postcode, q); };
  const cost = live?.postage ?? 0;

  return (
    <section id="postage" data-testid="postage-section" className="grain relative bg-[#112E23] px-5 py-24 text-[#F5F0E8] sm:px-8 lg:py-36">
      <div className="relative mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-7">
          <Reveal>
            <Eyebrow light>Postage · Australia-wide</Eyebrow>
            <h2 className="font-display text-6xl font-black uppercase leading-[0.88] tracking-tight sm:text-7xl">
              Flat rates, <span className="font-serif-i normal-case font-normal text-[#D99B26]">worked out fairly.</span>
            </h2>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-[#F5F0E8]/80 sm:text-lg">
              Australian postage is calculated at checkout based on your order and delivery location — a flat rate by number of balls and your state or territory.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-12 overflow-x-auto">
            <table data-testid="postage-rates-table" className="w-full min-w-[520px] text-left">
              <thead>
                <tr className="border-b border-[#F5F0E8]/20 font-mono text-[11px] uppercase tracking-[0.2em] text-[#F5F0E8]/70">
                  <th className="py-3 pr-4 font-normal">Zone</th>
                  {TIERS.map((t) => <th key={t} className="py-3 pr-4 font-normal">{t}</th>)}
                </tr>
              </thead>
              <tbody>
                {Object.keys(RATES).map((z) => (
                  <tr key={z} className="border-b border-[#F5F0E8]/10 transition-colors hover:bg-[#F5F0E8]/5">
                    <td className="py-4 pr-4 font-display text-xl font-bold uppercase">{ZONE_LABEL[z]}</td>
                    {RATES[z].map((r, i) => <td key={i} className="py-4 pr-4 font-mono text-sm">{aud(r)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-5">
          <div data-testid="postage-estimator" className="rounded-[28px] bg-[#F5F0E8] p-7 text-[#112E23] shadow-2xl sm:p-9">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#6E472B]">Postage estimator</div>
            <div className="mt-6 space-y-5">
              <div>
                <label className="mb-2 block text-sm font-semibold">Delivery postcode</label>
                <Input
                  data-testid="postage-postcode-input"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="e.g. 6230"
                  value={postcode}
                  onChange={(e) => onPostcode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  className="h-12 rounded-xl border-[#112E23]/20 bg-white"
                />
                {postcode.length === 4 && !live && err && <p data-testid="postage-postcode-error" className="mt-1.5 text-xs text-[#a3260f]">{err}</p>}
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold">Number of balls</label>
                <Stepper id="postage-qty" value={qty} onChange={onQty} />
              </div>
            </div>
              <div data-testid="postage-estimate-value-wrap" className="mt-8 flex items-end justify-between border-t border-[#112E23]/15 pt-6">
                <span className="text-sm text-[#112E23]/75">Estimated postage for {qty} ball{qty === 1 ? "" : "s"}</span>
                <motion.span key={cost} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} data-testid="postage-estimate-value" className="font-mono text-4xl font-semibold">
                  {aud(cost)}
                </motion.span>
              </div>
              <p data-testid="postage-per-ball-value" className="mt-2 text-right font-mono text-xs text-[#112E23]/70">
                {live ? `≈ ${aud(live.per_ball)} per ball · Australia Post price for postcode ${postcode}` : valid ? "Getting your Australia Post price…" : "Enter your postcode for a live Australia Post price"}
              </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
